import {Component, EventEmitter} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ModalComponent} from '../modal-service.service';
import { HttpClient } from '@angular/common/http';
import {AppConfigService} from '../../../service/app-config.service';
import {AlertModalService} from '../alert-modal/alert-modal.service';
import {ConfirmationModalService} from '../confirmation-modal/confirmation-modal.service';
import {catchError, EMPTY, Observable, of} from 'rxjs';
import {AttachmentModel} from 'src/app/common/models/attachment-model';
import {PlanAttachment} from '../../../models/plan-attachment.model';
import {PlanPdfService} from '../../../service/plan-pdf.service';
import {PlanModelService} from '../../../service/plan-model.service';
import {HTTP_STATUS_CODES, XMLHTTP_REQUEST_READY_STATE} from '../../../constants';
import {NgEventBus} from 'ng-event-bus';
import {switchMap} from 'rxjs/operators';

export interface fileInfo {
    url: string;
    uuid: string;
}

@Component({
    selector: 'nlr-attachments-modal',
    imports: [CommonModule],
    templateUrl: './attachments-modal.component.html',
    styleUrls: ['../modal.scss', './attachments-modal.component.scss']
})
export class AttachmentsModalComponent implements ModalComponent {
    modalClosed: EventEmitter<AttachmentModel | string> = new EventEmitter<string>();
    modalSize: string = 'modal-md';
    title: string = 'Upload attachment';
    fileToUpload: File;
    fileInfo: fileInfo = {url: '', uuid: ''};
    isSuffixValid: boolean = true;
    isFileNameValid: boolean = true;

    //TODO: The properties below should be passed from attachment component (caller, lookup info)
    extensions: string[];
    maxSizeBytes: number;
    prefix: string;
    attachments: PlanAttachment[];
    checkPdfSignature: boolean;
    isNotLandgateSigned: boolean;

    attachmentModel: AttachmentModel = {uuid: '', url: '', fileName: '', fileSize: 0};

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private alertModalService: AlertModalService,
        private planPdfService: PlanPdfService,
        private confirmationModalService: ConfirmationModalService,
        private planModelService: PlanModelService,
        private eventBus: NgEventBus
    ) {}

    close() {
        this.modalClosed.emit('close');
    }

    uploadFileChange(files: File[]) {
        this.fileToUpload = files[0];
    }

    validateAndUpload() {
        if (this.maxSizeBytes && this.fileToUpload.size > this.maxSizeBytes) {
            const errorMsg =
                'The size (' + this.fileToUpload.size + "B) of '" + this.fileToUpload.name + "' exceeds the maximum (" + this.maxSizeBytes + 'B)';
            console.error(errorMsg);
            this.alertModalService.createAlertMessage(errorMsg);
            return;
        }
        this.isSuffixValid = this.isValidSuffix(this.fileToUpload.name);

        if (this.planModelService.getModel().attachments) {
            this.isFileNameValid = !this.planModelService.getModel().attachments.some(attr => attr.fileName === this.fileToUpload.name);
        }

        if (this.isSuffixValid && this.isFileNameValid) {
            const fileName = this.fileToUpload.name.toLowerCase();
            const fileNameExtension = this.getFileSuffix(fileName).toLocaleLowerCase();
            const fileNameBaseTruncated = fileName.slice(0, Math.min(1024, fileName.length) - fileNameExtension.length);
            const fileNameTruncatedEncoded = encodeURIComponent(fileNameBaseTruncated + fileNameExtension);
            const url = this.appConfigService.settings.apiGatewayUrl + '/attachment/signedUrl/' + this.prefix + '/' + fileNameTruncatedEncoded;

            const fileReader = new FileReader();

            fileReader.onload = () => {
                const subscription = this.http
                    .get<fileInfo>(url)
                    .pipe(
                        switchMap(result => {
                            this.fileInfo.uuid = result.uuid;
                            this.fileInfo.url = result.url;

                            if (fileNameExtension === '.pdf') {
                                this.planModelService.isNotLandgateSigned = true;
                                if (this.checkPdfSignature) {
                                    return this.uploadPlanPdfAttachment(result.url, fileReader.result as ArrayBuffer);
                                }
                            }

                            return this.uploadFile(result.url, fileReader.result as ArrayBuffer);
                        }),
                        catchError(error => {
                            console.error(`${this.title} - validateAndUpload - error:`, error);
                            const errorMsg = 'System error while validating the attached file';
                            this.alertModalService.createAlertMessage(errorMsg);
                            return EMPTY;
                        })
                    )
                    .subscribe(() => {
                        //TODO: Update attachment details for attachment component
                        this.attachmentModel.uuid = this.fileInfo.uuid;
                        this.attachmentModel.url = this.fileInfo.url;
                        this.attachmentModel.fileName = this.fileToUpload.name;
                        this.attachmentModel.fileSize = this.fileToUpload.size;
                        this.modalClosed.emit(this.attachmentModel);
                    });

                this.eventBus.cast('nlr:busy', subscription);
            };

            fileReader.readAsArrayBuffer(this.fileToUpload);
        }
    }

    uploadPlanPdfAttachment(url: string, result: ArrayBuffer) {
        return this.planPdfService.checkIsLandgateSigned(this.arrayBufferToBase64(result)).pipe(
            switchMap((signed: boolean) => {
                if (!signed) {
                    this.modalClosed.emit('close');
                    return this.confirmationModalService
                        .showModal({
                            title: 'Warning',
                            confirmationText:
                                'You have attached a plan that has been generated outside the Portal.' +
                                " Select 'Yes' to confirm that you wish to lodge a manually generated plan PDF and CSD file," +
                                " or select 'No' if you would prefer to generate your plan in the Portal."
                        })
                        .pipe(
                            switchMap(action => {
                                return action === 'yes' ? this.uploadFile(url, result) : of();
                            })
                        );
                } else {
                    this.planModelService.isNotLandgateSigned = false;
                    return this.uploadFile(url, result);
                }
            })
        );
    }

    uploadFile(url: string, result: ArrayBuffer) {
        const xhr = new XMLHttpRequest();
        return new Observable(observer => {
            xhr.onreadystatechange = () => {
                if (xhr.readyState === XMLHTTP_REQUEST_READY_STATE.DONE) {
                    if (xhr.status === HTTP_STATUS_CODES.OK) {
                        observer.next(result);
                        observer.complete();
                    } else {
                        const errorMsg = 'The file did not save. Please try again. If the problem persists contact the Customer Services.';
                        observer.error(errorMsg);
                        this.alertModalService.createAlertMessage(errorMsg);
                    }
                }
            };
            xhr.open('PUT', url, true);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
            xhr.send(result);
        });
    }

    arrayBufferToBase64(result: ArrayBuffer) {
        let binary = '';
        const bytes = new Uint8Array(result);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    getFileSuffix(filename: string): string {
        if (filename === undefined) {
            return '';
        }

        const lastIndexOfDot = filename.lastIndexOf('.');

        if (lastIndexOfDot == -1) {
            return '';
        }

        return filename.slice(lastIndexOfDot).toUpperCase();
    }

    isValidSuffix(fileName: string): boolean {
        const upperCaseExtensions = this.extensions.map(value => {
            return value.toUpperCase();
        });

        return upperCaseExtensions.indexOf(this.getFileSuffix(fileName)) != -1;
    }
}
